/**
 * Attendance Service
 *
 * Only the endpoints that actually exist on the backend are exposed here.
 *
 * This file previously offered getAll / getById / create / update / delete /
 * bulkCreate / getByDate, all pointing at routes that were never implemented
 * server-side. Every one of them 404'd. `getByClass` in particular delegated
 * to `getAll`, which is why the coach attendance screen always came back
 * empty — it was asking for a route that does not exist instead of
 * /attendance/class/{id}.
 *
 * Real backend routes (verified against the running app):
 *   POST /attendance/mark
 *   GET  /attendance/summary/
 *   GET  /attendance/enrollment/{id}/history
 *   GET  /attendance/enrollment/{id}/streak
 *   GET  /attendance/stats/{child_id}
 *   GET  /attendance/class/{class_id}
 */

import apiClient from '../client';
import { API_ENDPOINTS } from '../../constants/api.constants';

const attendanceService = {
  /**
   * Roll call for one class on one date.
   * @param {string} classId
   * @param {string} [date] - YYYY-MM-DD; backend defaults to today
   */
  async getByClass(classId, date = null) {
    const { data } = await apiClient.get(API_ENDPOINTS.ATTENDANCE.CLASS(classId), {
      params: date ? { date } : {},
    });
    return data?.records || data || [];
  },

  /**
   * Mark attendance for one or more students in a class.
   * @param {Object} payload
   * @param {string} payload.class_id
   * @param {string} payload.date - YYYY-MM-DD
   * @param {Array<{enrollment_id: string, status: 'present'|'absent'|'excused', notes?: string}>} payload.records
   */
  async mark(payload) {
    const { data } = await apiClient.post(API_ENDPOINTS.ATTENDANCE.MARK, payload);
    return data;
  },

  /**
   * Mark one child for one day. The API wants the date INSIDE each record
   * and has no top-level date: a payload shaped the other way is refused.
   */
  async markOne({ classId, date, enrollmentId, status }) {
    return this.mark({
      class_id: classId,
      records: [{ enrollment_id: enrollmentId, date, status }],
    });
  },

  /** Attendance history for one enrollment. */
  async getByEnrollment(enrollmentId, filters = {}) {
    const { data } = await apiClient.get(
      API_ENDPOINTS.ATTENDANCE.HISTORY(enrollmentId),
      { params: filters },
    );
    return data;
  },

  /** Consecutive-attendance streak for one enrollment. */
  async getStreak(enrollmentId) {
    const { data } = await apiClient.get(
      API_ENDPOINTS.ATTENDANCE.STREAK(enrollmentId),
    );
    return data;
  },

  /** Aggregate attendance stats for one child. */
  async getStats(childId) {
    const { data } = await apiClient.get(API_ENDPOINTS.ATTENDANCE.STATS(childId));
    return data;
  },

  /** Present/absent/excused counts for a date range, optionally one class. */
  async getSummary(params) {
    const { data } = await apiClient.get(API_ENDPOINTS.ATTENDANCE.SUMMARY, {
      params,
    });
    return data;
  },
};

export default attendanceService;
